import type {
  RequestItem,
  RequestStatus,
  Driver,
  SavedLocation,
  SavedEquipment,
  Notification,
  User,
  Role,
  EquipmentKind,
  WeightClass,
  Dispute,
  DisputeStatus,
  Review,
  Quote,
  Invoice,
} from '../types/app';
import type {
  ApiRequest,
  ApiDriver,
  ApiNotification,
  ApiSavedLocation,
  ApiSavedEquipment,
  ApiUser,
  ApiDispute,
  ApiReview,
  ApiQuote,
  ApiInvoice,
} from '../types/api';

function adaptQuote(q: ApiQuote): Quote {
  return {
    handlingFee: q.handlingFee,
    transportFee: q.transportFee,
    permitFee: q.permitFee,
    surcharge: q.surcharge,
    total: q.total,
    validUntil: q.validUntil,
    counterOfferAmount: q.counterOfferAmount ?? null,
    counterOfferNote: q.counterOfferNote ?? null,
    counterOfferStatus: q.counterOfferStatus ?? 'NONE',
  };
}

const BACKEND_TO_FRONTEND_STATUS: Record<string, RequestStatus> = {
  PENDING: 'Pending',
  QUOTED: 'Quoted',
  ACCEPTED: 'Accepted',
  ASSIGNED: 'Assigned',
  EN_ROUTE: 'EnRoute',
  LOADING: 'Loading',
  IN_TRANSIT: 'InTransit',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  DISPUTED: 'Disputed',
};

export const FRONTEND_TO_BACKEND_STATUS: Record<string, string> = {
  Pending: 'PENDING',
  Quoted: 'QUOTED',
  Accepted: 'ACCEPTED',
  Assigned: 'ASSIGNED',
  EnRoute: 'EN_ROUTE',
  Loading: 'LOADING',
  InTransit: 'IN_TRANSIT',
  Delivered: 'DELIVERED',
  Cancelled: 'CANCELLED',
  Disputed: 'DISPUTED',
};

export const STATUS_LABELS: Record<RequestStatus, string> = {
  Pending: 'Pending',
  Quoted: 'Quoted',
  Accepted: 'Accepted',
  Assigned: 'Assigned',
  EnRoute: 'En Route to Pickup',
  Loading: 'Loading',
  InTransit: 'In Transit',
  Delivered: 'Delivered',
  Cancelled: 'Cancelled',
  Disputed: 'Disputed',
};

function resolveUser(
  ref: ApiUser | string | null | undefined,
): { id: string; name: string; phone?: string; company?: string } {
  if (!ref) return { id: '', name: '' };
  if (typeof ref === 'string') return { id: ref, name: '' };
  return { id: ref.id, name: ref.name, phone: ref.phone ?? undefined, company: ref.company ?? undefined };
}

export function adaptRequest(r: ApiRequest): RequestItem {
  const customer = resolveUser(r.customerId as ApiUser | string);
  const driver = r.driverId
    ? resolveUser((r.driverId as ApiDriver).userId as ApiUser | string)
    : null;

  const scheduledAt = r.scheduledAt ? new Date(r.scheduledAt) : new Date(r.createdAt);
  const dateLabel = scheduledAt.toLocaleDateString('en-JM', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  let timeSlot: string;
  if (r.isImmediate) {
    timeSlot = 'Immediate';
  } else if (r.deliveryTime) {
    const [h, m] = r.deliveryTime.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    timeSlot = `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  } else if (scheduledAt.getHours() !== 0 || scheduledAt.getMinutes() !== 0) {
    timeSlot = scheduledAt.toLocaleTimeString('en-JM', { hour: '2-digit', minute: '2-digit' });
  } else {
    timeSlot = '';
  }

  return {
    id: r.id,
    customerId: customer.id,
    customerName: customer.name,
    equipmentKind: (r.equipmentType ?? 'Custom') as EquipmentKind,
    equipmentDesc: r.equipmentNotes ?? '',
    make: r.make ?? undefined,
    model: r.model ?? undefined,
    estimatedWeight: r.estimatedWeight ?? undefined,
    photoUrls: r.photoUrls,
    pickupMedia: r.pickupMedia ?? [],
    deliveryMedia: r.deliveryMedia ?? [],
    weightClass: (r.weightClass ?? 'medium') as RequestItem['weightClass'],
    pickup: r.pickupAddress,
    dropoff: r.dropoffAddress,
    pickupLat: r.pickupLat,
    pickupLng: r.pickupLng,
    dropoffLat: r.dropoffLat,
    dropoffLng: r.dropoffLng,
    distanceKm: r.distanceKm ?? 0,
    dateLabel,
    timeSlot,
    contactName: r.contactName ?? customer.name,
    contactPhone: r.contactPhone ?? '',
    company: r.company ?? '',
    notes: r.loadingInstructions ?? '',
    status: (BACKEND_TO_FRONTEND_STATUS[r.status] ??
      (import.meta.env.DEV && console.warn(`[adapters] unknown status: ${r.status}`),
      'Pending')) as RequestStatus,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    assignedDriverId: driver?.id || undefined,
    assignedDriverName: driver?.name,
    assignedDriverPhone: driver?.phone,
    quotedTotal: r.quotedTotal,
    quote: r.quote ? adaptQuote(r.quote) : null,
  };
}

const DRIVER_STATUS_MAP: Record<ApiDriver['status'], Driver['status']> = {
  AVAILABLE: 'available',
  ON_JOB: 'on_job',
  OFFLINE: 'offline',
};

export function adaptDriver(d: ApiDriver): Driver {
  const user = d.name
    ? { id: d.id, name: d.name, phone: d.phone ?? undefined, email: d.email, suspended: d.suspended }
    : resolveUser(d.userId as ApiUser | string);
  return {
    id: d.id,
    userId: user.id,
    name: user.name || 'Unknown Driver',
    phone: user.phone ?? '',
    status: DRIVER_STATUS_MAP[d.status] ?? 'available',
    licenseNumber: d.licenseNumber,
    licenseExpiry: d.licenseExpiry,
    totalJobs: d.totalJobs ?? 0,
  };
}

const NOTIFICATION_TYPE_MAP: Record<string, Notification['type']> = {
  REQUEST: 'info',
  QUOTE: 'success',
  TRACKING: 'info',
  SYSTEM: 'warning',
  CHAT: 'info',
  DISPUTE: 'warning',
};

export function adaptNotification(n: ApiNotification): Notification {
  return {
    id: n.id,
    title: n.title,
    body: n.body,
    type: NOTIFICATION_TYPE_MAP[n.type] ?? 'info',
    read: n.read,
    createdAt: n.createdAt,
    data: n.data,
  };
}

export function adaptSavedLocation(l: ApiSavedLocation): SavedLocation {
  return {
    id: l.id,
    label: l.label,
    address: l.address,
    lat: l.lat ?? undefined,
    lng: l.lng ?? undefined,
  };
}

export function adaptSavedEquipment(e: ApiSavedEquipment): SavedEquipment {
  return {
    id: e.id,
    label: e.label,
    equipmentKind: e.equipmentKind as EquipmentKind,
    equipmentDesc: e.equipmentDesc,
    weightClass: e.weightClass as WeightClass,
    make: e.make,
    model: e.model,
    estimatedWeight: e.estimatedWeight ?? undefined,
    photoUrl: e.photoUrl ?? undefined,
  };
}

export function adaptUser(u: ApiUser): User {
  const [first = '', last = ''] = u.name.split(' ');
  const initials =
    ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || u.name[0]?.toUpperCase() || 'U';
  return {
    id: u.id,
    name: u.name,
    email: u.email,
    phone: u.phone ?? undefined,
    role: u.role.toLowerCase() as Role,
    company: u.company ?? undefined,
    avatarInitials: initials,
    avatarUrl: u.avatarUrl ?? undefined,
    suspended: u.suspended ?? false,
    notificationPrefs: u.notificationPrefs,
    onboardingCompleted: u.onboardingCompleted ?? false,
  };
}

const DISPUTE_STATUS_MAP: Record<ApiDispute['status'], DisputeStatus> = {
  OPEN: 'Open',
  UNDER_REVIEW: 'UnderReview',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

export const FRONTEND_TO_BACKEND_DISPUTE_STATUS: Record<DisputeStatus, ApiDispute['status']> = {
  Open: 'OPEN',
  UnderReview: 'UNDER_REVIEW',
  Resolved: 'RESOLVED',
  Closed: 'CLOSED',
};

export function adaptDispute(d: ApiDispute): Dispute {
  const req = typeof d.requestId === 'string' ? null : (d.requestId as ApiRequest);
  const filer = typeof d.filedById === 'string' ? null : (d.filedById as ApiUser);
  return {
    id: d.id,
    requestId: typeof d.requestId === 'string' ? d.requestId : (req?.id ?? ''),
    requestLabel: req
      ? (req.equipmentType ?? req.id)
      : typeof d.requestId === 'string'
        ? d.requestId
        : '',
    filedByName: filer?.name ?? 'Unknown',
    description: d.description,
    evidence: d.evidence,
    status: DISPUTE_STATUS_MAP[d.status] ?? 'Open',
    resolution: d.resolution,
    createdAt: d.createdAt,
  };
}

const REVIEW_STATUS_MAP: Record<ApiReview['status'], Review['status']> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export function adaptReview(r: ApiReview): Review {
  const reviewer = resolveUser(r.reviewerId as ApiUser | string);
  const reviewee = resolveUser(r.revieweeId as ApiUser | string);
  return {
    id: r.id,
    requestId: typeof r.requestId === 'string' ? r.requestId : (r.requestId as ApiRequest).id,
    reviewerName: reviewer.name || 'Unknown',
    reviewerCompany: reviewer.company,
    revieweeId: reviewee.id,
    revieweeName: reviewee.name || 'Unknown',
    rating: r.rating,
    comment: r.comment,
    status: REVIEW_STATUS_MAP[r.status] ?? 'Pending',
    createdAt: r.createdAt,
  };
}

export function adaptInvoice(inv: ApiInvoice): Invoice {
  const req = typeof inv.requestId === 'string' ? null : (inv.requestId as ApiRequest);
  return {
    id: inv.id,
    requestId: typeof inv.requestId === 'string' ? inv.requestId : (req?.id ?? ''),
    requestLabel: req
      ? (req.equipmentType ?? req.id)
      : typeof inv.requestId === 'string'
        ? inv.requestId
        : '',
    total: inv.total,
    status: inv.status,
    issuedAt: inv.issuedAt,
    dueAt: inv.dueAt,
    lineItems: inv.lineItems,
  };
}
